import { Card, CardBody, Col, Container, Row } from 'reactstrap';
import Breadcrumbs from '../../coreComponents/breadcrumbs';
import CommonCardHeader from '../../coreComponents/CommonCardHeader';
import { useTranslation } from 'react-i18next';
import BasicForm from '../formsAndTable/forms/formControls/baseInput/BasicForm';
import { createBot, getBotTranscriptionFiles, leaveMeetingBot } from '../../services/api';
import { useEffect, useState } from 'react';
import AjaxSourcedData from '../formsAndTable/tables/dataTables/dataSource/AjaxSourcedData';
import { getBotFilesTableColumns } from '../../data/formsAndTable/tables/dataTables/DataSource';

import SimpleModal from '../uiKits/modal/basicModal/SimpleModal';
import Swal from 'sweetalert2';
import { Primary } from '../../constants';
import { NoDataComponent } from '../formsAndTable/forms/formControls/NoDataComponent';

const MeetBotPage = () => {
  const { t } = useTranslation();
  const [botId, setBotId] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [transcriptionFiles, setTranscriptionFiles] = useState([]);
  const [botLoading, setBotLoading] = useState(false);
  const [linkError, setLinkError] = useState(false);

  const checkGoogleMeetLink = (link: string) => {
    const regex = /^(https?:\/\/)?(meet\.google\.com)?\/[a-zA-Z0-9-]{10,}$/;
    setLinkError(!regex.test(link));
  };

  const submitHandler = (data: { meetLink: string; botName: string; botLang: string }) => {
    checkGoogleMeetLink(data.meetLink);
    if (linkError) {
      Swal.fire({
        text: t('bot.invalidMeetLinkFormat'),
        confirmButtonText: 'Ok',
        confirmButtonColor: Primary,
      });
      return;
    }
    createMeetBot(data);
  };
  const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const createMeetBot = async (data: { meetLink: string; botName: string; botLang: string }) => {
    setBotLoading(true);
    const botData = {
      meetLink: data.meetLink,
      botName: data.botName,
      botLang: data.botLang,
    };
    const bot = await createBot(botData);
    if (bot) {
      setBotId(bot.id);
      Swal.fire({
        text: t('bot.botCreatedTitle'),
        confirmButtonText: 'Ok',
        confirmButtonColor: Primary,
      });
    }
    setBotLoading(false);
  };

  const leaveMeeting = async () => {
    if (!botId) {
      alert('No bot id');
      return;
    }
    const leave = await leaveMeetingBot(botId);
    if (leave) {
      setBotId('');
      Swal.fire({
        text: t('bot.botLeftMeeting'),
        confirmButtonText: 'Ok',
        confirmButtonColor: Primary,
      });
    }
  };

  const meetBotInputs = [
    {
      slug: 'botName',
      label: t('bot.formInputs.labelBotName'),
      title: t('bot.formInputs.titleBotName'),
      placeholder: t('bot.formInputs.placeholderBotName'),
      value: '',
      type: 'text' as const,
      required: true,
      validator: (value: string) => {
        if (!value || value.trim() === '') {
          return 'Invalid bot name';
        }
        return null;
      },
      disabled: botId !== '',
    },

    {
      slug: 'meetLink',
      label: t('bot.formInputs.labelMeetLink'),
      title: t('bot.formInputs.titleMeetTitle'),
      placeholder: t('bot.formInputs.placeholderMeetTitle'),
      value: '',
      type: 'text' as const,
      required: true,
      validator: (value: string) => {
        checkGoogleMeetLink(value);
        if (!value || value.trim() === '' || linkError) {
          return 'Invalid Google Meet link format';
        }
        return null;
      },
      disabled: botId !== '',
    },
    {
      id: 'bot_uk',
      slug: 'botLang',
      label: t('bot.formInputs.radioLabelUkrainian'),
      title: t('bot.formInputs.radioLabelUkrainian'),
      text: t('bot.formInputs.radioLabelUkrainian'),
      value: 'uk',
      type: 'radio' as const,
      groupTitle: t('bot.formInputs.radioGroutLanguageTitle'),
      required: true,
      disabled: botId !== '',
    },
    {
      groupTitle: t('bot.formInputs.radioGroutLanguageTitle'),
      id: 'bot_ru',
      slug: 'botLang',
      label: t('bot.formInputs.radioLabelRussian'),
      title: t('bot.formInputs.radioLabelRussian'),
      text: t('bot.formInputs.radioLabelRussian'),
      value: 'ru',
      type: 'radio' as const,
      required: true,
      disabled: botId !== '',
    },
  ];

  const formButtons = [
    {
      type: 'submit',
      title: t('global.ok'),
      color: 'success',
      disabledTitle: botLoading ? 'Loading...' : 'Ok',
      disabled: botLoading || botId !== '',
    },
    {
      type: 'button',
      title: t('courses.exam.disconnectBot'),
      color: 'danger',
      clickHandler: leaveMeeting,
      disabled: !botId || botLoading,
    },
  ];
  useEffect(() => {
    const fetchTranscriptionFiles = async () => {
      try {
        const files = await getBotTranscriptionFiles();
        setTranscriptionFiles(files.files || []);
      } catch (err) {
        console.error('Failed to load transcription files', err);
      }
    };

    fetchTranscriptionFiles();
  }, []);
  return (
    <>
      <Breadcrumbs mainTitle={t('bot.botPageHeader')} />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card className="position-relative">
              <CommonCardHeader title={t('bot.botPageHeader')} tag />

              <CardBody>
                {' '}
                <Col sm="6">
                  <BasicForm
                    inputs={meetBotInputs}
                    formTitle={t('bot.formTitle')}
                    formSubTitle={t('bot.formSubTitle')}
                    submitHandler={submitHandler}
                    buttons={formButtons}
                    // data={defaultFormValues}
                    // defaultValues={defaultFormValues}
                  />
                </Col>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Row>
          <Col sm="12">
            <Card className="position-relative">
              <CommonCardHeader title={t('bot.botFilesTableTitle')} tag />

              <CardBody>
                {transcriptionFiles.length && (
                  <AjaxSourcedData
                    data={transcriptionFiles}
                    columns={getBotFilesTableColumns(t, viewerTz)}
                    noDataComponent={<NoDataComponent />}
                    // title="All courses"
                    // subTitle={
                    //   Array.isArray(CoursesListTableSubtitle)
                    //     ? CoursesListTableSubtitle.map((sub) => sub.text).join(
                    //         ", "
                    //       )
                    //     : CoursesListTableSubtitle
                    // }
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <SimpleModal isOpen={isOpen} close={() => setIsOpen(false)}>
        <Row>
          {/* <BasicTimeline timeLineHeader={'Bot loading stages'} timeLineData={timeLineData} /> */}
        </Row>
      </SimpleModal>
    </>
  );
};

export default MeetBotPage;
